/* eslint-disable sonarjs/no-duplicate-string */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_PLOT_GROUP_SORT,
  PlotGroupSort,
  PlotGroupSortEnum,
  SortOptions,
} from '@/api/rest/resources/types/plotGroup';
import { DefaultWidgetNames, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { useSetPlotGroupSort } from '@/components/PlotGroups/hooks/useSetPlotGroup';
import { useActivePlotGroup } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { DialogWidgetHandle } from '@/components/widgets/o/Dialog/Dialog';
import { useMatchCurrentPage } from '@/hooks/useMatchCurrentPage';
import { usePathSet } from '@/hooks/usePathSet';
import { isNullish } from '@/utils/booleans';

export const usePlotGroupSortConfiguration = () => {
  const { t } = useTranslation();

  const activePlotGroup = useActivePlotGroup();
  const { setPlotGroupSort } = useSetPlotGroupSort();
  const dialogRef = useRef<DialogWidgetHandle>();

  const handleSetPlotGroupSort = (sort: PlotGroupSort) => {
    setPlotGroupSort(sort);
    dialogRef.current?.closeDialog();
  };

  const activeSort = useGetActiveSort();
  const activeSortsText = activeSort.map(({ key, value }) => {
    const keyLabel = t(`global.widget.filters.sections.${key}`, { defaultValue: key });
    const valueLabel = t(getValueStringMapping(value, key), { defaultValue: value });

    return `${keyLabel} (${valueLabel})`;
  });

  const sortButtonText =
    activeSortsText?.length > 0
      ? `${t('global.widget.filters.button.sortBy')} ${activeSortsText.join(', ')}`
      : t('global.widget.filters.button.sort');

  const applicableSortSections = useGetApplicableSortSections();

  return {
    name: DefaultWidgetNames.default_plot_group_sort,
    type: WidgetType.o_c_plot_group_sort,
    widgets: [
      {
        name: 'clear_sort_filters',
        type: WidgetType.a_button,
        relatedData: {
          i18nPath: 'global.widget.filters.button.clearSorting',
          variant: 'text',
          className: 'w-fit underline',
          size: 'small',
          onClick: () => {
            setPlotGroupSort(DEFAULT_PLOT_GROUP_SORT);
          },
        },
        visible: activeSort?.length > 0,
      },
      {
        name: DefaultWidgetNames.default_plot_group_sort,
        type: WidgetType.o_dialog,
        relatedData: {
          button: {
            icon: 'ri-arrow-up-down-fill',
            text: sortButtonText,
            variant: 'outline',
            className: 'w-fit truncate',
          },
          ref: dialogRef,
        },
        visible: true,
        widgets: [
          {
            name: 'sort_filters_dialog_content',
            type: WidgetType.o_dialog_content,
            visible: true,
            relatedData: {
              withXButton: false,
              className: 'py-4 px-6',
            },
            widgets: [
              {
                name: 'sort_filters_form',
                type: WidgetType.o_form,
                visible: true,
                relatedData: {
                  defaultValues: activePlotGroup.sort,
                  onSubmit: handleSetPlotGroupSort,
                },
                widgets: [
                  {
                    name: 'sort_filters_dialog_header',
                    type: WidgetType.o_dialog_header,
                    visible: true,
                    relatedData: {
                      className: 'flex flex-row justify-between items-center border-b border-divider mb-0',
                    },
                    widgets: [
                      {
                        name: 'sort_filters_dialog_header_text',
                        type: WidgetType.a_text,
                        visible: true,
                        relatedData: {
                          i18nPath: 'global.widget.filters.dialog.sortBy',
                          className: 'typography-body1Semibold',
                        },
                      },
                      {
                        name: 'sort_filters_dialog_header_text_close',
                        type: WidgetType.o_dialog_close,
                        visible: true,
                        relatedData: {
                          className: 'pr-0',
                        },
                      },
                    ],
                  },
                  {
                    name: 'sort_filters_dialog_body',
                    type: WidgetType.o_dialog_body,
                    visible: true,
                    relatedData: {
                      className: 'max-h-[65vh] overflow-y-auto',
                    },
                    widgets: applicableSortSections,
                  },
                  {
                    name: 'sort_filters_dialog_footer',
                    type: WidgetType.o_dialog_footer,
                    visible: true,
                    widgets: [
                      {
                        name: 'sort_filters_dialog_footer_stack_container',
                        type: WidgetType.a_stack,
                        relatedData: {
                          className: 'w-full justify-end pt-4',
                          direction: 'row',
                          centerMain: true,
                          spacing: 4,
                        },
                        visible: true,
                        widgets: [
                          {
                            name: 'sort_filters_dialog_footer_submit_button',
                            type: WidgetType.o_dialog_close,
                            visible: true,
                            relatedData: {
                              showIcon: false,
                              i18nPath: 'global.widget.filters.button.close',
                              type: 'button',
                              variant: 'text',
                              className: 'w-fit aspect-auto',
                            },
                          },
                          {
                            name: 'sort_filters_dialog_footer_submit_button',
                            type: WidgetType.o_form_submit_button,
                            visible: true,
                            relatedData: {
                              i18nPath: 'global.widget.filters.button.showResults',
                              className: 'w-fit',
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  } as WidgetSlotConfiguration;
};

type SortSectionProps = {
  sort: keyof typeof PlotGroupSortEnum;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  translate: any;
  translationFallback: {
    asc: string;
    desc: string;
  };
};

const getFilterSection = ({ sort, translate, translationFallback }: SortSectionProps) => {
  const formName = PlotGroupSortEnum[`${sort}`];

  return {
    name: `sort_filters_dialog_body_section_${sort}`,
    type: WidgetType.o_form_section,
    visible: true,
    relatedData: {
      spacing: 2,
      className: 'py-4 border-b border-divider',
      label: {
        i18nPath: `global.widget.filters.sections.${sort}`,
        className: 'uppercase typography-overline',
      },
      section: {
        className: 'w-fit',
        centerMain: true,
        direction: 'row',
        spacing: 2,
      },
      formName,
    },
    widgets: [
      {
        name: `${sort}_radio_group`,
        type: WidgetType.o_form_radio_group,
        visible: true,
        relatedData: {
          name: formName,
          className: 'flex-row w-full gap-16',
        },
        widgets: [
          {
            name: `sort_filters_dialog_body_section_${sort}_item`,
            type: WidgetType.o_form_radio_item,
            visible: true,
            relatedData: {
              data: [
                {
                  label: translate(getValueStringMapping(SortOptions.desc, formName), {
                    defaultValue: translationFallback.desc,
                  }),
                  value: SortOptions.desc,
                },
                {
                  label: translate(getValueStringMapping(SortOptions.asc, formName), {
                    defaultValue: translationFallback.asc,
                  }),
                  value: SortOptions.asc,
                },
              ],
            },
            repeat: {
              data: 'relatedData.data',
              key: 'value',
              template: {
                name: `sort_filters_dialog_body_section_${sort}_item`,
                type: WidgetType.o_form_radio_item,
                visible: true,
                relatedData: {
                  formName,
                  removeFormItems: Object.values(PlotGroupSortEnum).filter((key) => key !== sort),
                },
              },
            },
          },
        ],
      },
    ],
  };
};

const useGetActiveSort = () => {
  const { sort } = useActivePlotGroup();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Object.entries(sort).reduce<Array<{ key: string; value: any }>>((acc, [key, value]) => {
    if (isNullish(value)) return acc;

    return [...acc, { key, value }];
  }, []);
};

const getValueStringMapping = (value: string, key: string) => {
  switch (key) {
    case PlotGroupSortEnum.plot__name:
      return `global.widget.filters.sections.sortOptions.aZ.${value}`;
    case PlotGroupSortEnum.plot__area:
      return `global.widget.filters.sections.sortOptions.largeToSmall.${value}`;
    case PlotGroupSortEnum.ag_carbon:
      return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    case PlotGroupSortEnum.bg_carbon:
      return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    case PlotGroupSortEnum.water:
      return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    case PlotGroupSortEnum.pofh:
      return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    case PlotGroupSortEnum.ag_carbon_trend:
      return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    case PlotGroupSortEnum.bg_carbon_trend:
      return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    case PlotGroupSortEnum.water_trend:
      return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    case PlotGroupSortEnum.pofh_trend:
      return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    case PlotGroupSortEnum.stewarded_value:
      return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    // TODO: MVP-CW - Uncomment and implement these fields when needed
    // case PlotGroupSortEnum.assetName:
    //   return `global.widget.filters.sections.sortOptions.aZ.${value}`;
    // case PlotGroupSortEnum.country:
    //   return `global.widget.filters.sections.sortOptions.aZ.${value}`;
    // case PlotGroupSortEnum.hectares:
    //   return `global.widget.filters.sections.sortOptions.largeToSmall.${value}`;
    // case PlotGroupSortEnum.stewardValue:
    //   return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    // case PlotGroupSortEnum.valueAtRisk:
    //   return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    // case PlotGroupSortEnum.natureAtRisk:
    //   return `global.widget.filters.sections.sortOptions.lowToHigh.${value}`;
    default:
      return value;
  }
};

const useGetApplicableSortSections = () => {
  const { t } = useTranslation();
  const pathSet = usePathSet();

  const defaultSections = [
    getFilterSection({
      sort: 'plot__name',
      translate: t,
      translationFallback: {
        asc: 'A-Z',
        desc: 'Z-A',
      },
    }),
    getFilterSection({
      sort: 'plot__area',
      translate: t,
      translationFallback: {
        asc: 'Low to high',
        desc: 'High to low',
      },
    }),
    getFilterSection({
      sort: 'stewarded_value',
      translate: t,
      translationFallback: {
        asc: 'Low to high',
        desc: 'High to low',
      },
    }),
  ];
  const carbonSections = [
    getFilterSection({
      sort: 'ag_carbon',
      translate: t,
      translationFallback: {
        asc: 'Low to high',
        desc: 'High to low',
      },
    }),
    getFilterSection({
      sort: 'ag_carbon_trend',
      translate: t,
      translationFallback: {
        asc: 'Low to high',
        desc: 'High to low',
      },
    }),
    getFilterSection({
      sort: 'bg_carbon',
      translate: t,
      translationFallback: {
        asc: 'Low to high',
        desc: 'High to low',
      },
    }),
    getFilterSection({
      sort: 'bg_carbon_trend',
      translate: t,
      translationFallback: {
        asc: 'Low to high',
        desc: 'High to low',
      },
    }),
  ];

  const waterSections = [
    getFilterSection({
      sort: 'water',
      translate: t,
      translationFallback: {
        asc: 'Low to high',
        desc: 'High to low',
      },
    }),
    getFilterSection({
      sort: 'water_trend',
      translate: t,
      translationFallback: {
        asc: 'Low to high',
        desc: 'High to low',
      },
    }),
  ];

  const pofhSections = [
    getFilterSection({
      sort: 'pofh',
      translate: t,
      translationFallback: {
        asc: 'Low to high',
        desc: 'High to low',
      },
    }),
    getFilterSection({
      sort: 'pofh_trend',
      translate: t,
      translationFallback: {
        asc: 'Low to high',
        desc: 'High to low',
      },
    }),
  ];

  const hasCarbon = useMatchCurrentPage([pathSet.carbonDetails, pathSet.soilDetails]);
  const hasWater = useMatchCurrentPage([pathSet.waterDetails]);
  const hasPofh = useMatchCurrentPage([pathSet.biodiversityDetails]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let customSections: Record<string, any>[] = [];
  if (hasCarbon) {
    customSections = carbonSections;
  } else if (hasWater) {
    customSections = waterSections;
  } else if (hasPofh) {
    customSections = pofhSections;
  } else {
    customSections = [...carbonSections, ...waterSections, ...pofhSections];
  }

  return [...defaultSections, ...customSections];
};
